import { useEffect, useRef } from 'react';

interface AdSenseBannerProps {
  slot?: string;
  format?: 'auto' | 'fluid' | 'rectangle' | 'horizontal' | 'vertical';
  responsive?: boolean;
  className?: string;
}

interface InternalProps extends AdSenseBannerProps {
  /** Only render when the page has real publisher content loaded — defaults to false for safety */
  hasContent?: boolean;
  /** Minimum number of content items on the page before showing ads */
  minContentItems?: number;
  /** Actual count of content items on the page */
  contentCount?: number;
}

const AdSenseBanner = ({
  slot = '',
  format = 'auto',
  responsive = true,
  className = '',
  hasContent = false,
  minContentItems = 3,
  contentCount = 0,
}: InternalProps) => {
  const adRef = useRef<HTMLDivElement>(null);
  const pushed = useRef(false);

  // Require either explicit hasContent=true OR contentCount >= minContentItems
  const shouldShow = hasContent || contentCount >= minContentItems;

  useEffect(() => {
    if (!shouldShow || pushed.current) return;
    try {
      if (typeof window !== 'undefined' && (window as any).adsbygoogle) {
        (window as any).adsbygoogle.push({});
        pushed.current = true;
      }
    } catch {
      // AdSense not loaded yet
    }
  }, [shouldShow]);

  // Don't render ad at all if there's insufficient publisher content
  if (!shouldShow) return null;

  return (
    <div className={`ad-container my-4 flex items-center justify-center min-h-[100px] bg-muted/20 rounded-2xl overflow-hidden border border-border/30 ${className}`} ref={adRef}>
      <ins
        className="adsbygoogle"
        style={{ display: 'block', width: '100%' }}
        data-ad-client="ca-pub-1055318583834402"
        data-ad-slot={slot}
        data-ad-format={format}
        data-full-width-responsive={responsive ? 'true' : 'false'}
      />
    </div>
  );
};

export default AdSenseBanner;
